/**
 * CreateJobPage Component
 * Dedicated page for creating new analysis jobs
 *
 * UPDATED:
 * - Category types changed from "general"/"hotel" to "standard"/"advanced"
 * - Added support for config-based jobs
 * - Uses acceptedExtensions for better helper text
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, FileUpload, ErrorAlert } from "../components/common";
import { JobTypeGrid } from "../components/jobs/JobTypeGrid";
import {
  CameraConfigForm,
  validateCameraConfig,
} from "../components/jobs/CameraConfigForm";
import { JobType, CameraConfig } from "../types";
import {
  JOB_TYPE_CONFIGS,
  isConfigBasedJobType,
  hasExternalLink,
  getExternalLink,
} from "../services/jobTypes";
import { getJobIcon } from "../utils/icons";
import { validateJobFile } from "../utils/validation.utils";
import { ROUTES } from "../services/routes";
import { useAppDispatch, jobsActions, uiActions } from "../redux";

export const CreateJobPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [step, setStep] = useState<"select" | "upload">("select");
  const [jobType, setJobType] = useState<JobType | "">("");
  const [file, setFile] = useState<File | null>(null);
  const [cameraConfig, setCameraConfig] = useState<CameraConfig[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleSelectJobType = (selectedType: JobType) => {
    // Check if job type has external link (like LIDAR)
    if (hasExternalLink(selectedType)) {
      const link = getExternalLink(selectedType);
      if (link) {
        window.open(link, "_blank", "noopener,noreferrer");
      }
      return;
    }

    setJobType(selectedType);
    setStep("upload");
  };

  const handleBack = () => {
    if (step === "upload") {
      setStep("select");
      setFile(null);
      setCameraConfig([]);
      setError(null);
    } else {
      navigate(ROUTES.DASHBOARD);
    }
  };

  const isConfigBased = jobType
    ? isConfigBasedJobType(jobType as JobType)
    : false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!jobType) {
      setError("Please select a job type");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const config = JOB_TYPE_CONFIGS[jobType as JobType];

      if (isConfigBased) {
        // Validate camera configuration
        const configValidation = validateCameraConfig(cameraConfig);
        if (!configValidation.isValid) {
          setError(configValidation.error || "Invalid configuration");
          setLoading(false);
          return;
        }

        // Create config-based job
        await dispatch(
          jobsActions.createConfigJob({
            jobType: jobType as JobType,
            config: { cameras: cameraConfig },
          })
        ).unwrap();
      } else {
        // Validate file
        if (!file) {
          setError("Please select a file");
          setLoading(false);
          return;
        }

        const fileValidation = validateJobFile(
          file,
          config.acceptedFileTypes,
          config.maxFileSize
        );

        if (!fileValidation.isValid) {
          setError(fileValidation.error || "Invalid file");
          setLoading(false);
          return;
        }

        // Create file-based job
        await dispatch(
          jobsActions.createJob({
            jobType: jobType as JobType,
            file: file,
            onProgress: (progress) => {
              setUploadProgress(progress);
              dispatch(uiActions.setUploadProgress(progress));
            },
          })
        ).unwrap();
      }

      // Show success notification
      dispatch(
        uiActions.addNotification({
          type: "success",
          message: isConfigBased
            ? "Camera system configured successfully!"
            : "Job created successfully! Processing will begin shortly.",
        })
      );

      // Navigate to dashboard
      navigate(ROUTES.DASHBOARD);
    } catch (err: any) {
      setError(err.message || err || "Failed to create job");
    } finally {
      setLoading(false);
      dispatch(uiActions.resetUploadProgress());
    }
  };

  const selectedConfig = jobType ? JOB_TYPE_CONFIGS[jobType as JobType] : null;

  // Get helper text for file upload
  const getHelperText = () => {
    if (!selectedConfig) return "";

    const extensions =
      selectedConfig.acceptedExtensions.length > 0
        ? selectedConfig.acceptedExtensions.join(", ")
        : selectedConfig.acceptedFileTypes
            .map((type) => type.split("/")[1])
            .join(", ");

    return `Accepted formats: ${extensions} • Max size: ${selectedConfig.maxFileSize}MB`;
  };

  // Get upload label based on job type
  const getUploadLabel = () => {
    if (!selectedConfig) return "Upload File";

    if (
      selectedConfig.acceptedExtensions?.some((ext) =>
        [".stl", ".obj", ".ply", ".step", ".stp"].includes(ext.toLowerCase())
      )
    ) {
      return "Upload 3D Model";
    }

    return "Upload Video or Image";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors"
            disabled={loading}
          >
            <span>◀️</span>
            <span>Back</span>
          </button>

          <h1 className="text-4xl font-bold text-white mb-2">
            Create New Analysis
          </h1>
          <p className="text-slate-400">
            {step === "select"
              ? "Select the type of analysis you want to perform"
              : isConfigBased
              ? "Configure your cameras to begin monitoring"
              : "Upload your media file to begin analysis"}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              step === "select"
                ? "bg-cyan-500/20 border border-cyan-500/30"
                : "bg-slate-700/50 border border-slate-600/30"
            }`}
          >
            <span className="text-2xl">1️⃣</span>
            <span className="text-white font-medium">Select Type</span>
          </div>

          <div className="w-12 h-0.5 bg-slate-600" />

          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              step === "upload"
                ? "bg-cyan-500/20 border border-cyan-500/30"
                : "bg-slate-700/50 border border-slate-600/30"
            }`}
          >
            <span className="text-2xl">2️⃣</span>
            <span className="text-white font-medium">
              {isConfigBased ? "Configure" : "Upload File"}
            </span>
          </div>
        </div>

        {/* Content */}
        {step === "select" ? (
          <JobTypeGrid onSelectJobType={handleSelectJobType} />
        ) : (
          <div className="max-w-3xl mx-auto">
            <Card padding="lg">
              {/* Error Alert */}
              {error && (
                <ErrorAlert
                  message={error}
                  onDismiss={() => setError(null)}
                  className="mb-6"
                />
              )}

              {/* Selected Job Type Info */}
              {selectedConfig && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-6 mb-6">
                  <div className="flex items-start gap-4">
                  <div className="text-5xl">{getJobIcon(selectedConfig.iconName, "w-12 h-12")}</div>
                    <div className="flex-1">
                      <h3 className="text-cyan-400 font-semibold text-xl mb-2">
                        {selectedConfig.label}
                      </h3>
                      <p className="text-cyan-300 mb-3">
                        {selectedConfig.description}
                      </p>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block px-2 py-1 text-xs font-medium rounded-md ${
                            selectedConfig.category === "advanced"
                              ? "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                              : "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                          }`}
                        >
                          {selectedConfig.category === "advanced"
                            ? "Advanced"
                            : "Standard"}
                        </span>
                        {selectedConfig.externalService && (
                          <span className="inline-block px-2 py-1 text-xs font-medium rounded-md bg-purple-500/20 text-purple-400 border border-purple-500/30">
                            External API
                          </span>
                        )}
                        {selectedConfig.isConfigBased && (
                          <span className="inline-block px-2 py-1 text-xs font-medium rounded-md bg-violet-500/20 text-violet-400 border border-violet-500/30">
                            Config-Based
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Conditional Input: File Upload OR Config Form */}
              {selectedConfig && (
                <>
                  {isConfigBased ? (
                    <CameraConfigForm
                      onConfigChange={setCameraConfig}
                      maxCameras={10}
                    />
                  ) : (
                    <FileUpload
                      label={getUploadLabel()}
                      accept={selectedConfig.acceptedFileTypes}
                      maxSize={selectedConfig.maxFileSize}
                      onFileSelect={setFile}
                      onError={(err) => setError(err)}
                      helperText={getHelperText()}
                    />
                  )}
                </>
              )}

              {/* Upload Progress (file-based jobs only) */}
              {loading && !isConfigBased && uploadProgress > 0 && (
                <div className="space-y-2 mt-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Uploading...</span>
                    <span className="text-cyan-400 font-medium">
                      {uploadProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div
                      className="bg-cyan-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Processing indicator (config-based jobs) */}
              {loading && isConfigBased && (
                <div className="flex items-center justify-center py-4 mt-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                  <span className="ml-3 text-slate-400">
                    Configuring cameras...
                  </span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 mt-8">
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  disabled={loading}
                  fullWidth
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={
                    !jobType ||
                    loading ||
                    (isConfigBased ? cameraConfig.length === 0 : !file)
                  }
                  loading={loading}
                  fullWidth
                  icon="🚀"
                >
                  {isConfigBased ? "Configure Cameras" : "Create Job"}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
